import { useEffect, useState, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { STAGE_LABELS } from "@/lib/constants";
import SLABadge from "@/components/SLABadge";
import { MessageSquare, Star, StarOff, ChevronDown, Save, Trash2, Eye } from "lucide-react";

// Kept in sync with backend `_can_edit_tracker` in routers/tracker.py.
const EDIT_ROLES = new Set([
  "contract_analyst", "attorney", "general_counsel",
  "legal_manager", "contract_manager", "it_admin",
]);

// Column catalog — id maps to render logic below. Sticky first two are always shown.
// Role → prioritized column set. Empty = show all. When a role has an entry,
// TrackerTable's "Reset to my role default" button applies exactly this set.
const ROLE_DEFAULT_COLUMNS = {
  compliance:      ["description","department","status","comments","evaluation","analyst","atty","sla"],
  records_management: ["description","department","executed","total","status","atty","sla"],
  attorney:        ["description","department","sla","days_kickoff","atty","analyst","status","comments"],
  contract_analyst:["description","department","timeline","atty","analyst","days_kickoff","status","comments","sla"],
  general_counsel: ["description","department","status","atty","analyst","sla","days_kickoff","total"],
  legal_manager:   ["description","department","atty","analyst","sla","days_kickoff","status","total","evaluation"],
};

const ALL_COLUMNS = [
  { id: "description",    label: "Description",       w: 220 },
  { id: "hcd_entity",     label: "HCD Entity",        w: 140 },
  { id: "department",     label: "Department",        w: 140 },
  { id: "timeline",       label: "Timeline",          w: 100 },
  { id: "bu_rep",         label: "BU Rep",            w: 160 },
  { id: "initial_received", label: "Initial Received", w: 130 },
  { id: "new_received",   label: "New Received",      w: 130 },
  { id: "kicked_off",     label: "Kicked Off",        w: 130 },
  { id: "days_kickoff",   label: "Days",              w: 70, num: true },
  { id: "atty",           label: "Atty",              w: 140 },
  { id: "analyst",        label: "Analyst",           w: 140 },
  { id: "comments",       label: "Comments",          w: 200 },
  { id: "status",         label: "Status",            w: 140 },
  { id: "evaluation",     label: "AI Eval",           w: 140 },
  // Reviewer slot pair columns are injected dynamically here.
  { id: "vendor_review",  label: "Vendor Review",     w: 130 },
  { id: "days_vendor",    label: "Days",              w: 70, num: true },
  { id: "total_days_legal", label: "Total Days in Legal", w: 110, num: true },
  { id: "executed",       label: "Executed",          w: 130 },
  { id: "days_executed",  label: "Days",              w: 70, num: true },
  { id: "total",          label: "Total",             w: 70, num: true },
  { id: "sla",            label: "SLA",               w: 110 },
];

const fmtDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toISOString().slice(0, 10); } catch { return ""; }
};

function DateCell({ value, editable, onSave, testId }) {
  const [v, setV] = useState(fmtDate(value));
  useEffect(() => { setV(fmtDate(value)); }, [value]);
  if (!editable) return <span className="text-xs text-slate-600">{fmtDate(value) || "—"}</span>;
  return (
    <input type="date" data-testid={testId} value={v}
           onChange={(e) => setV(e.target.value)}
           onBlur={() => { if (fmtDate(value) !== v) onSave(v || null); }}
           className="h-7 px-1 border border-slate-200 rounded text-xs w-32 bg-transparent hover:bg-white focus:bg-white"/>
  );
}

function StatusCell({ value, fallback, editable, onSave, testId }) {
  const [v, setV] = useState(value || "");
  useEffect(() => { setV(value || ""); }, [value]);
  if (!editable) return <span className="text-xs">{value || fallback}</span>;
  return (
    <input type="text" data-testid={testId} value={v}
           onChange={(e) => setV(e.target.value)}
           onBlur={() => { if ((value || "") !== v) onSave(v || null); }}
           placeholder={fallback}
           className="h-7 px-1 border border-slate-200 rounded text-xs w-32 bg-transparent hover:bg-white focus:bg-white"/>
  );
}

function CommentCell({ comment, testId }) {
  const [open, setOpen] = useState(false);
  if (!comment) return <span className="text-xs text-slate-300">—</span>;
  return (
    <div className="relative" data-testid={testId}>
      <button onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
              className="flex items-center gap-1 text-xs text-slate-700 hover:text-[#005289] max-w-[180px] truncate"
              title={comment.body}>
        <MessageSquare className="w-3 h-3 flex-shrink-0"/>
        <span className="truncate">{comment.snippet}</span>
      </button>
      {open ? (
        <div onClick={e => e.stopPropagation()}
             className="absolute z-30 left-0 top-full mt-1 w-72 p-3 bg-white border border-slate-200 rounded-md shadow-lg text-xs"
             data-testid={`${testId}-popover`}>
          <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-1">
            {comment.author || "Internal note"} · {comment.at ? new Date(comment.at).toLocaleString() : ""}
          </div>
          <div className="text-slate-700 whitespace-pre-wrap">{comment.body}</div>
          <div className="text-right mt-2">
            <button onClick={() => setOpen(false)} className="text-[10px] text-slate-500 hover:text-slate-800">Close</button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

const TIMELINE_CLASS = {
  "21-day":    "bg-blue-50 text-blue-700 border-blue-200",
  "40-day":    "bg-slate-50 text-slate-700 border-slate-200",
  "Expedited": "bg-red-50 text-red-700 border-red-200",
};

export default function TrackerTable({ rows, onChanged, testIdPrefix = "tt", emptyText = "Nothing here.", selection = null, onToggle = null, actionColumn = null }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const canEdit = user && EDIT_ROLES.has(user.role);
  const showSelect = selection && onToggle;
  const stickyLeftBase = showSelect ? 36 : 0;

  const [slots, setSlots] = useState([]);
  const [views, setViews] = useState([]);
  const [visibleCols, setVisibleCols] = useState(null); // null = all
  const [visibleSlots, setVisibleSlots] = useState(null);
  const [filters, setFilters] = useState({ status: "", department: "", timeline: "" });
  const [viewMenu, setViewMenu] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState("");

  useEffect(() => {
    api.get("/reviewer-slots").then(({ data }) => setSlots(data || []));
    api.get("/saved-views?scope=tracker").then(({ data }) => {
      setViews(data || []);
      const def = (data || []).find(v => v.is_default);
      if (def) applyView(def);
    }).catch(() => {});
    // eslint-disable-next-line
  }, []);

  const patch = async (id, body) => {
    try { await api.patch(`/contracts/${id}/tracker`, body); toast.success("Saved"); onChanged && onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const applyView = (v) => {
    const cols = v.filters?.visible_columns;
    const slotIds = v.filters?.visible_slots;
    const f = v.filters?.filters;
    setVisibleCols(cols && Array.isArray(cols) ? new Set(cols) : null);
    setVisibleSlots(slotIds && Array.isArray(slotIds) ? new Set(slotIds) : null);
    setFilters({
      status: f?.status || "",
      department: f?.department || "",
      timeline: f?.timeline || "",
    });
    setViewMenu(false);
  };

  const isColOn = (id) => !visibleCols || visibleCols.has(id);
  const isSlotOn = (sid) => !visibleSlots || visibleSlots.has(sid);

  const saveView = async () => {
    if (!saveName.trim()) return toast.error("Name required");
    const filtersPayload = {
      scope: "tracker",
      visible_columns: ALL_COLUMNS.filter(c => isColOn(c.id)).map(c => c.id),
      visible_slots: (slots || []).filter(s => isSlotOn(s.id)).map(s => s.id),
      filters,
    };
    try {
      const { data } = await api.post("/saved-views", { name: saveName.trim(), filters: filtersPayload });
      setViews(vs => [data, ...vs]);
      setSaveOpen(false); setSaveName("");
      toast.success("View saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const setDefault = async (v) => {
    try {
      await api.patch(`/saved-views/${v.id}`, { is_default: true });
      setViews(vs => vs.map(x => ({ ...x, is_default: x.id === v.id })));
      toast.success("Default view updated");
    } catch { toast.error("Failed"); }
  };

  const deleteView = async (v) => {
    if (!window.confirm(`Delete view "${v.name}"?`)) return;
    try { await api.delete(`/saved-views/${v.id}`); setViews(vs => vs.filter(x => x.id !== v.id)); toast.success("Deleted"); }
    catch { toast.error("Delete failed"); }
  };

  const clearView = () => { setVisibleCols(null); setVisibleSlots(null); setFilters({ status: "", department: "", timeline: "" }); setViewMenu(false); };

  const resetToRoleDefault = () => {
    const preset = user && ROLE_DEFAULT_COLUMNS[user.role];
    if (!preset) {
      toast.info("No role default configured — showing all columns");
      clearView();
      return;
    }
    setVisibleCols(new Set(preset));
    setVisibleSlots(null);
    setFilters({ status: "", department: "", timeline: "" });
    setViewMenu(false);
    toast.success(`Applied ${user.role.replace(/_/g, " ")} default columns`);
  };

  // "Save My Default" — persist the caller's current column/slot/filter choice
  // as their personal default (scope=tracker, is_default=true). Reused on every
  // reload via the useEffect above.
  const saveMyDefault = async () => {
    const filtersPayload = {
      scope: "tracker",
      visible_columns: ALL_COLUMNS.filter(c => isColOn(c.id)).map(c => c.id),
      visible_slots: (slots || []).filter(s => isSlotOn(s.id)).map(s => s.id),
      filters,
      is_default: true,
    };
    try {
      const existing = views.find(v => v.is_default && v.name === "My Default");
      if (existing) await api.delete(`/saved-views/${existing.id}`);
      const { data } = await api.post("/saved-views", { name: "My Default", filters: filtersPayload });
      setViews(vs => [data, ...vs.filter(v => v.name !== "My Default")]);
      toast.success("Saved as your personal default");
      setViewMenu(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const toggleCol = (id) => {
    const s = new Set(visibleCols || ALL_COLUMNS.map(c => c.id));
    s.has(id) ? s.delete(id) : s.add(id);
    setVisibleCols(s);
  };
  const toggleSlotVisibility = (sid) => {
    const s = new Set(visibleSlots || (slots || []).map(x => x.id));
    s.has(sid) ? s.delete(sid) : s.add(sid);
    setVisibleSlots(s);
  };

  // Build sorted list of visible slot columns (each slot = date + days pair)
  const activeSlots = (slots || []).filter(s => isSlotOn(s.id));

  // Apply row-level filters (in-memory).
  const filteredRows = rows.filter(r => {
    if (filters.status && !(r.stage === filters.status || r.queue_status === filters.status)) return false;
    if (filters.department && r.department !== filters.department) return false;
    if (filters.timeline && r.timeline !== filters.timeline) return false;
    return true;
  });

  // Distinct filter option sets from rows (so the dropdowns only show what's present).
  const uniq = (arr) => Array.from(new Set(arr.filter(Boolean))).sort();
  const statusOpts = uniq(rows.flatMap(r => [r.stage, r.queue_status]));
  const deptOpts = uniq(rows.map(r => r.department));
  const timelineOpts = uniq(rows.map(r => r.timeline));
  const filterActive = filters.status || filters.department || filters.timeline;

  return (
    <div className="border border-slate-200 rounded-md bg-white overflow-hidden" data-testid={`${testIdPrefix}-container`}>
      {/* Header toolbar for Saved Views */}
      <div className="px-3 py-2 border-b border-slate-200 bg-slate-50/50 flex items-center gap-2 text-xs">
        <div className="relative">
          <button onClick={() => setViewMenu(v => !v)} data-testid={`${testIdPrefix}-views-btn`}
                  className="h-7 px-2 border border-slate-200 rounded flex items-center gap-1 bg-white hover:bg-slate-50">
            <Eye className="w-3 h-3"/> Views
            <ChevronDown className="w-3 h-3"/>
          </button>
          {viewMenu ? (
            <div className="absolute z-30 top-full left-0 mt-1 w-72 bg-white border border-slate-200 rounded-md shadow-lg p-2" data-testid={`${testIdPrefix}-views-menu`}>
              <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold px-2 py-1">Saved views</div>
              {(views || []).length === 0 ? (
                <div className="text-slate-400 px-2 py-3 text-center">No saved views yet.</div>
              ) : (views || []).map(v => (
                <div key={v.id} className="flex items-center gap-1 hover:bg-slate-50 rounded px-2 py-1">
                  <button onClick={() => applyView(v)} className="flex-1 text-left truncate" data-testid={`${testIdPrefix}-view-load-${v.id}`}>
                    {v.name}
                  </button>
                  <button onClick={() => setDefault(v)} title="Set default" className="text-slate-400 hover:text-amber-500" data-testid={`${testIdPrefix}-view-star-${v.id}`}>
                    {v.is_default ? <Star className="w-3 h-3 text-amber-500 fill-amber-500"/> : <StarOff className="w-3 h-3"/>}
                  </button>
                  <button onClick={() => deleteView(v)} title="Delete" className="text-slate-400 hover:text-red-500" data-testid={`${testIdPrefix}-view-del-${v.id}`}>
                    <Trash2 className="w-3 h-3"/>
                  </button>
                </div>
              ))}
              <div className="border-t border-slate-100 mt-2 pt-2 flex items-center gap-1">
                <button onClick={clearView} className="text-[11px] px-2 py-1 rounded hover:bg-slate-100 text-slate-600" data-testid={`${testIdPrefix}-view-clear`}>Show all</button>
                <button onClick={() => { setViewMenu(false); setSaveOpen(true); }} data-testid={`${testIdPrefix}-view-save-open`}
                        className="ml-auto text-[11px] px-2 py-1 rounded bg-[#005289] text-white flex items-center gap-1">
                  <Save className="w-3 h-3"/> Save current
                </button>
              </div>
            </div>
          ) : null}
        </div>
        {visibleCols || visibleSlots || filterActive ? (
          <span className="text-[10px] text-slate-500">Filtered view active</span>
        ) : null}
        <select value={filters.status} onChange={e => setFilters(f => ({...f, status: e.target.value}))}
                data-testid={`${testIdPrefix}-filter-status`}
                className="h-7 px-2 border border-slate-200 rounded text-[11px] bg-white">
          <option value="">All stages</option>
          {statusOpts.map(s => <option key={s} value={s}>{STAGE_LABELS[s] || s}</option>)}
        </select>
        <select value={filters.department} onChange={e => setFilters(f => ({...f, department: e.target.value}))}
                data-testid={`${testIdPrefix}-filter-dept`}
                className="h-7 px-2 border border-slate-200 rounded text-[11px] bg-white">
          <option value="">All departments</option>
          {deptOpts.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={filters.timeline} onChange={e => setFilters(f => ({...f, timeline: e.target.value}))}
                data-testid={`${testIdPrefix}-filter-timeline`}
                className="h-7 px-2 border border-slate-200 rounded text-[11px] bg-white">
          <option value="">All timelines</option>
          {timelineOpts.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <details className="ml-auto text-[11px]">
          <summary className="cursor-pointer text-slate-500 hover:text-slate-800">Columns ▾</summary>
          <div className="absolute right-3 z-20 mt-2 w-64 bg-white border border-slate-200 rounded-md shadow-lg p-3 max-h-96 overflow-y-auto">
            <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-2">Toggle columns</div>
            {user && ROLE_DEFAULT_COLUMNS[user.role] && (
              <button onClick={resetToRoleDefault} type="button"
                      data-testid="tt-reset-role-default"
                      className="w-full mb-2 text-[11px] font-semibold text-[#005289] border border-[#005289]/30 rounded px-2 py-1 hover:bg-[#005289]/5">
                Reset to my {user.role.replace(/_/g, " ")} default
              </button>
            )}
            <button onClick={saveMyDefault} type="button"
                    data-testid="tt-save-my-default"
                    className="w-full mb-2 text-[11px] font-semibold text-emerald-700 border border-emerald-200 rounded px-2 py-1 hover:bg-emerald-50">
              Save current as My Default
            </button>
            {ALL_COLUMNS.map(c => (
              <label key={c.id} className="flex items-center gap-2 py-0.5">
                <input type="checkbox" checked={isColOn(c.id)} onChange={() => toggleCol(c.id)}/>
                <span>{c.label}</span>
              </label>
            ))}
            {(slots || []).length ? (
              <>
                <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mt-3 mb-1">Reviewer slots</div>
                {slots.map(s => (
                  <label key={s.id} className="flex items-center gap-2 py-0.5">
                    <input type="checkbox" checked={isSlotOn(s.id)} onChange={() => toggleSlotVisibility(s.id)}/>
                    <span>{s.role_label} — {s.name}</span>
                  </label>
                ))}
              </>
            ) : null}
          </div>
        </details>
      </div>

      <div className="overflow-x-auto">
        <table className="text-xs min-w-[2000px] border-collapse">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
            <tr>
              {showSelect ? <th className="sticky left-0 z-20 bg-slate-50 w-9 px-2 py-2 border-r border-slate-200"></th> : null}
              <th className="sticky z-20 bg-slate-50 text-left px-3 py-2 w-[88px] border-r border-slate-200" style={{ left: stickyLeftBase }}>#</th>
              <th className="sticky z-20 bg-slate-50 text-left px-3 py-2 w-[220px] border-r border-slate-200" style={{ left: stickyLeftBase + 88 }}>Vendor</th>
              {ALL_COLUMNS.slice(0, 13).map(c => isColOn(c.id) ? (
                <th key={c.id} className={`text-${c.num ? "right" : "left"} px-3 py-2`} style={{ width: c.w }}>{c.label}</th>
              ) : null)}
              {/* Dynamic reviewer slot columns */}
              {activeSlots.map(s => (
                <Fragment key={s.id}>
                  <th className="text-left px-3 py-2 w-[140px]">{s.role_label} — {s.name}</th>
                  <th className="text-right px-3 py-2 w-[70px]">Days</th>
                </Fragment>
              ))}
              {ALL_COLUMNS.slice(13).map(c => isColOn(c.id) ? (
                <th key={c.id} className={`text-${c.num ? "right" : "left"} px-3 py-2`} style={{ width: c.w }}>{c.label}</th>
              ) : null)}
              {actionColumn ? <th className="text-right px-3 py-2 w-[130px]">Action</th> : null}
            </tr>
          </thead>
          <tbody data-testid={`${testIdPrefix}-rows`}>
            {filteredRows.length === 0 ? (
              <tr><td colSpan={26} className="p-8 text-center text-slate-400">{emptyText}</td></tr>
            ) : null}
            {filteredRows.map(c => {
              const stageLbl = STAGE_LABELS[c.stage] || c.stage;
              const rowGo = () => nav(`/contract/${c.id}`);
              const isSel = showSelect && selection.has(c.id);
              const slotDates = c.reviewer_slot_dates || {};
              const slotDays = c.slot_days || {};
              return (
                <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50 group" data-testid={`${testIdPrefix}-row-${c.contract_number}`}>
                  {showSelect ? (
                    <td className="sticky left-0 z-10 bg-white group-hover:bg-slate-50 px-2 py-2 border-r border-slate-100">
                      <input type="checkbox" data-testid={`row-check-${c.contract_number}`} checked={isSel} onChange={() => onToggle(c.id)}/>
                    </td>
                  ) : null}
                  <td onClick={rowGo} className="sticky z-10 bg-white group-hover:bg-slate-50 px-3 py-2 font-mono text-[11px] cursor-pointer border-r border-slate-100" style={{ left: stickyLeftBase }}>
                    {c.parent_contract_number ? (
                      <span className="text-slate-400 mr-1" title={`Linked ${c.linkage_relation || (c.revision_number ? "amendment" : "child")} of #${c.parent_contract_number}`}>└─</span>
                    ) : null}
                    {c.contract_number}
                    {c.parent_contract_number ? (
                      <span className="ml-1 inline-block text-[9px] font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded px-1"
                            title={`Parent: #${c.parent_contract_number}`}>
                        {(c.linkage_relation || (c.revision_number ? "AMEND" : "LINK")).slice(0, 5).toUpperCase()}
                      </span>
                    ) : null}
                  </td>
                  <td onClick={rowGo} className="sticky z-10 bg-white group-hover:bg-slate-50 px-3 py-2 font-semibold cursor-pointer border-r border-slate-100 max-w-[220px] truncate" style={{ left: stickyLeftBase + 88 }}>{c.vendor_legal_name}</td>

                  {isColOn("description") ? <td className="px-3 py-2 text-slate-600 max-w-[220px] truncate" title={c.description || c.purpose}>{c.description || c.purpose || "—"}</td> : null}
                  {isColOn("hcd_entity") ? <td className="px-3 py-2">{c.hcd_entity || "—"}</td> : null}
                  {isColOn("department") ? <td className="px-3 py-2">{c.department || "—"}</td> : null}
                  {isColOn("timeline") ? (
                    <td className="px-3 py-2">
                      <span className={`px-1.5 py-0.5 border rounded text-[10px] font-semibold ${TIMELINE_CLASS[c.timeline] || TIMELINE_CLASS["21-day"]}`}>{c.timeline || "21-day"}</span>
                    </td>
                  ) : null}
                  {isColOn("bu_rep") ? <td className="px-3 py-2">{c.bu_approver_name || "—"}</td> : null}
                  {isColOn("initial_received") ? <td className="px-3 py-2 text-slate-600">{fmtDate(c.initial_received_date) || "—"}</td> : null}
                  {isColOn("new_received") ? (
                    <td className="px-3 py-2"><DateCell value={c.new_received_date} editable={canEdit} onSave={(v) => patch(c.id, { new_received_date: v })} testId={`${testIdPrefix}-new-${c.contract_number}`}/></td>
                  ) : null}
                  {isColOn("kicked_off") ? (
                    <td className="px-3 py-2"><DateCell value={c.kicked_off_date} editable={canEdit} onSave={(v) => patch(c.id, { kicked_off_date: v })} testId={`${testIdPrefix}-kickoff-${c.contract_number}`}/></td>
                  ) : null}
                  {isColOn("days_kickoff") ? <td className="px-3 py-2 text-right font-mono">{c.days_since_kickoff ?? 0}</td> : null}
                  {isColOn("atty") ? <td className="px-3 py-2">{c.assigned_attorney_name || "—"}</td> : null}
                  {isColOn("analyst") ? <td className="px-3 py-2">{c.assigned_analyst_name || "—"}</td> : null}
                  {isColOn("comments") ? (
                    <td className="px-3 py-2"><CommentCell comment={c.latest_internal_comment} testId={`${testIdPrefix}-cmt-${c.contract_number}`}/></td>
                  ) : null}
                  {isColOn("status") ? (
                    <td className="px-3 py-2"><StatusCell value={c.queue_status} fallback={stageLbl} editable={canEdit} onSave={(v) => patch(c.id, { queue_status: v })} testId={`${testIdPrefix}-status-${c.contract_number}`}/></td>
                  ) : null}
                  {isColOn("evaluation") ? (
                    <td className="px-3 py-2" data-testid={`${testIdPrefix}-eval-${c.contract_number}`}>
                      {c.latest_evaluation_risk ? (
                        <button onClick={(e) => { e.stopPropagation(); nav(`/contract/${c.id}?tab=evaluation`); }}
                                className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded border ${
                                  c.latest_evaluation_risk === "High" ? "bg-red-50 text-red-700 border-red-200" :
                                  c.latest_evaluation_risk === "Moderate" ? "bg-amber-50 text-amber-700 border-amber-200" :
                                  "bg-emerald-50 text-emerald-700 border-emerald-200"
                                }`}>
                          <span>{c.latest_evaluation_risk}</span>
                          <span className="opacity-60">·</span>
                          <span>{c.latest_evaluation_score ?? 0}</span>
                          {c.latest_evaluation_status && c.latest_evaluation_status !== "Pending Review" ? (
                            <span className="ml-1 text-[9px] opacity-70">({c.latest_evaluation_status})</span>
                          ) : null}
                        </button>
                      ) : <span className="text-[10px] text-slate-300">—</span>}
                    </td>
                  ) : null}
                  {/* Reviewer slot cells (dynamic) */}
                  {activeSlots.map(s => (
                    <Fragment key={s.id}>
                      <td className="px-3 py-2">
                        <DateCell value={slotDates[s.id]} editable={canEdit}
                                  onSave={(v) => patch(c.id, { reviewer_slot_dates: { [s.id]: v } })}
                                  testId={`${testIdPrefix}-slot-${s.id}-${c.contract_number}`}/>
                      </td>
                      <td className="px-3 py-2 text-right font-mono">{slotDays[s.id] ?? 0}</td>
                    </Fragment>
                  ))}
                  {isColOn("vendor_review") ? (
                    <td className="px-3 py-2"><DateCell value={c.vendor_review_date} editable={canEdit} onSave={(v) => patch(c.id, { vendor_review_date: v })} testId={`${testIdPrefix}-vend-${c.contract_number}`}/></td>
                  ) : null}
                  {isColOn("days_vendor") ? <td className="px-3 py-2 text-right font-mono">{c.days_vendor_review ?? 0}</td> : null}
                  {isColOn("total_days_legal") ? <td className="px-3 py-2 text-right font-mono font-semibold">{c.total_days_in_legal ?? 0}</td> : null}
                  {isColOn("executed") ? (
                    <td className="px-3 py-2"><DateCell value={c.executed_date} editable={canEdit} onSave={(v) => patch(c.id, { executed_date: v })} testId={`${testIdPrefix}-exec-${c.contract_number}`}/></td>
                  ) : null}
                  {isColOn("days_executed") ? <td className="px-3 py-2 text-right font-mono">{c.days_since_executed ?? 0}</td> : null}
                  {isColOn("total") ? <td className="px-3 py-2 text-right font-mono font-semibold">{c.total_days_in_legal ?? 0}</td> : null}
                  {isColOn("sla") ? <td className="px-3 py-2"><SLABadge status={c.sla_status} days={c.sla_remaining}/></td> : null}

                  {actionColumn ? <td className="px-3 py-2 text-right whitespace-nowrap">{actionColumn(c)}</td> : null}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {saveOpen ? (
        <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center" onClick={() => setSaveOpen(false)} data-testid={`${testIdPrefix}-save-modal`}>
          <div className="bg-white rounded-md w-[380px] max-w-[90vw]" onClick={e => e.stopPropagation()}>
            <div className="px-4 py-3 border-b border-slate-200 font-bold text-sm">Save current view</div>
            <div className="p-4 space-y-2">
              <label className="text-xs block">
                <span className="font-bold text-slate-700 block mb-1">View name</span>
                <input value={saveName} onChange={e => setSaveName(e.target.value)} autoFocus data-testid={`${testIdPrefix}-view-save-name`}
                       className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md" placeholder="e.g., My triage view"/>
              </label>
              <div className="text-[11px] text-slate-500">
                Saves currently-visible columns and reviewer slots. You can set it as your default later.
              </div>
            </div>
            <div className="px-4 py-3 border-t border-slate-200 flex justify-end gap-2">
              <button onClick={() => setSaveOpen(false)} className="h-8 px-3 text-xs border border-slate-200 rounded">Cancel</button>
              <button onClick={saveView} data-testid={`${testIdPrefix}-view-save-btn`} className="h-8 px-3 text-xs rounded bg-[#005289] text-white font-semibold">Save</button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
